import { createPortal } from 'react-dom';
import { ApiError } from '../lib/api.js';
import { ConfirmDialog } from './ConfirmDialog.js';

/**
 * ehr#482 / #500, then ehr#711 (Jay 10-30): a phone number, email or SSN another chart of this
 * practice already has is refused (409 DUPLICATE_CONTACT). The pop-up says a chart already exists
 * with that information and offers View chart, but only to someone who may open that chart
 * (canOpen); anyone else is told to ask an admin, and is never given its name.
 */
export type DuplicateField = 'email' | 'phone' | 'ssn';

export type DuplicateContact =
  | {
      canOpen?: true;
      id: string;
      name: string;
      category: string;
      archived: boolean;
      field: DuplicateField;
      /** For a phone: the taken number's last 10 digits (which of the phones sent to show in red). */
      phoneKey?: string;
    }
  | { canOpen: false; field: DuplicateField; phoneKey?: string };

/** ehr#711: the chart this refusal may name and open (null for someone without access). */
export function openableDup(
  d: DuplicateContact | null,
): { id: string; name: string; archived: boolean } | null {
  return d && d.canOpen !== false && typeof d.id === 'string' ? d : null;
}

/** The field the refusal is about, in words. */
export const DUP_FIELD_LABEL: Record<DuplicateField, string> = {
  email: 'email',
  phone: 'phone number',
  ssn: 'SSN',
};

/** ehr#711: the line for someone who can't open the chart (no name, no details). */
export const NO_ACCESS_MESSAGE =
  'A chart with this information already exists in this practice. Ask an admin to give you access.';

/** The contact named by a DUPLICATE_CONTACT refusal, else null. */
export function duplicateOf(e: unknown): DuplicateContact | null {
  if (!(e instanceof ApiError) || e.code !== 'DUPLICATE_CONTACT') return null;
  const c = (e.details as { contact?: DuplicateContact } | undefined)?.contact;
  if (!c || typeof c.field !== 'string') return null;
  return c.canOpen === false || typeof (c as { id?: unknown }).id === 'string' ? c : null;
}

/** A phone's comparable form, as the server matches it: its last 10 digits ('' under 7). */
export const dupPhoneKey = (v: string | undefined | null): string => {
  const d = (v ?? '').replace(/\D/g, '');
  return d.length >= 7 ? d.slice(-10) : '';
};

/**
 * ehr#482: is this phone the taken one? With no phoneKey (an older server), any filled phone the
 * save sent counts.
 */
export function isDupPhone(dup: DuplicateContact | null, value: string | undefined): boolean {
  if (!dup || dup.field !== 'phone') return false;
  const k = dupPhoneKey(value);
  return Boolean(k) && (!dup.phoneKey || k === dup.phoneKey);
}

/**
 * ehr#482 (Chase, 10-18): "make it a pop-up in red", not a line at the bottom of the window.
 * ehr#711 (Jay 10-30): "a chart already exists with this information in it", naming which field
 * matched, then "Would you like to view it?": View chart (opens it; an archived chart opens too,
 * where it can be unarchived) or Cancel (back to the form; the field stays red). Someone who can't
 * open that chart gets the ask-an-admin line and one button, and never its name.
 */
export function DuplicateContactDialog({
  dup,
  onOpen,
  onClose,
}: {
  dup: DuplicateContact;
  onOpen?: (id: string) => void;
  onClose: () => void;
}) {
  const what = DUP_FIELD_LABEL[dup.field] ?? 'information';
  const chart = openableDup(dup);
  const canView = Boolean(chart && onOpen);
  // On <body>, so no window or drawer it is opened from (a transform, a clipped footer) holds it.
  return createPortal(
    <ConfirmDialog
      className="dup-contact-pop"
      alert
      message={
        <>
          <h3 className="dup-pop-title">
            <span className="dup-pop-icon" aria-hidden>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </span>
            A chart already exists with this information
          </h3>
          {chart ? (
            <p className="dup-pop-body">
              <strong>{chart.name}</strong>
              {chart.archived ? ' (archived)' : ''} already has this <strong>{what}</strong>
              {'. '}
              {canView
                ? chart.archived
                  ? 'Would you like to view it? You can unarchive it from there.'
                  : 'Would you like to view it?'
                : 'Change it to save.'}
            </p>
          ) : (
            <p className="dup-pop-body">
              {NO_ACCESS_MESSAGE} It matched the <strong>{what}</strong> you entered.
            </p>
          )}
        </>
      }
      confirmLabel={canView ? 'View chart' : 'OK'}
      cancelLabel="Cancel"
      hideCancel={!canView}
      onConfirm={() => (canView && chart ? onOpen!(chart.id) : onClose())}
      onClose={onClose}
    />,
    document.body,
  );
}
